import { Question, QuestionType, ModuleId } from '../types';
import { MODULES } from '../modules';
import { SEED_COLLECTIONS, getAllSeedQuestions, SeedQuestion, initializeSeedCollections, isSeedSystemReady } from './seedConsolidator';
import { error as logError, debug, group, groupEnd } from '../../utils/logger';
import { assertCanonicalSeedFeedback, isCitoEligible, validateQuestion } from './questionValidation';
import { validateQuestionData } from './dataSchemas';

// Re-export from split modules for backward compatibility
export { fisherYatesShuffle, shuffleQuestionOptions, shuffleQuestionWithExplanations } from './questionShuffling';
export { assertCanonicalSeedFeedback, isCitoEligible, validateQuestion } from './questionValidation';
// NOTE: citoSelection.ts was archived in v4.0 audit — orphaned module not used by assessment system.
// See docs/audits/SYSTEM_LOGIC_AUDIT_2026.md §4.4 for details.

/**
 * Question Loader — Taxonomy-Authoritative
 *
 * Single source of truth: 10 module-based seed collections.
 * Tier is authoritative.
 * All questions have required taxonomy fields.
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/data/questionLoader.ts in R27.
 * Split into 4 modules in R38: questionLoader (core), questionShuffling,
 * questionValidation, citoSelection.
 *
 * v4.2 (mei 2026): Removed redundant ALL_EXPLANATIONS map — feedback/aids
 * are already included in ALL_QUESTIONS_CORE via seedToCore(). Eliminated
 * triple-copy pattern in getAllQuestions(). ~40% memory reduction for question data.
 */

// ============================================================
// Seed → QuestionCore Conversion
// ============================================================

/**
 * Convert a SeedQuestion to Question (the public question type).
 */
function seedToCore(seed: SeedQuestion): Question {
  assertCanonicalSeedFeedback(seed);
  return {
    id: seed.id,
    module: seed.module,
    question: seed.question,
    questionType: seed.questionType as QuestionType,
    citoDifficulty: seed.citoDifficulty,
    citoDiscrimination: seed.citoDiscrimination,
    skill: seed.skill,
    subSkill: seed.subSkill,
    familyId: seed.familyId,
    context: seed.context,
    options: seed.options,
    correctAnswer: seed.correctAnswer,
    isAnchor: seed.isAnchor,
    data: seed.data,
    feedback: seed.feedback,
    stimulusAids: seed.stimulusAids,
    supportAids: seed.supportAids,
    // Taxonomy-authoritative fields (tier is canonical)
    tier: seed.tier,
    levelCode: seed.levelCode,
    group: seed.group,
    conceptId: seed.conceptId,
    variantIndex: seed.variantIndex,
    // FG2 Phase 2 — equivalence class for breadth/spread collapsing (§S10).
    equivalenceClass: seed.equivalenceClass,
    // Pedagogical contract (PEDAGOGICAL_CONTRACT_SPEC §3)
    pedagogy: seed.pedagogy
  };
}

// ============================================================
// Core Question Arrays + Indexes (Populated by initializeQuestionLoader)
// ============================================================

/** All questions as Question (normalized). Populated during init. */
export const ALL_QUESTIONS_CORE: Question[] = [];

// ============================================================
// PRE-BUILT INDEXES (Populated by initializeQuestionLoader)
// ============================================================

/** Lookup a single question by ID */
export const QUESTION_BY_ID: Map<string, Question> = new Map();

/** All questions for a given module */
export const QUESTIONS_BY_MODULE: Map<ModuleId, Question[]> = new Map();

/** Questions for a module+subSkill combination */
export const QUESTIONS_BY_MODULE_SUBSKILL: Map<string, Question[]> = new Map();

/** Questions grouped by familyId */
export const QUESTIONS_BY_FAMILY: Map<string, Question[]> = new Map();

/** All unique subSkills per module */
export const SUBSKILLS_BY_MODULE: Map<ModuleId, Set<string>> = new Map();

/** Questions by module+tier (authoritative index) */
export const QUESTIONS_BY_MODULE_TIER: Map<string, Question[]> = new Map();

/** Whether the question loader has been initialized (all modules) */
let _questionLoaderReady = false;

/** Set of module IDs whose questions have been added to indexes */
const _loadedModuleIds = new Set<ModuleId>();

/** Check if a module's questions have been added to the indexes */
export function isModuleLoaderReady(moduleId: ModuleId): boolean {
  return _loadedModuleIds.has(moduleId);
}

/**
 * Add a single module's seed collection into ALL_QUESTIONS_CORE and all
 * indexes. Idempotent — does nothing if the module is already loaded.
 *
 * Mutates the shared exports in-place so existing consumers see the new data.
 */
export function addModuleToIndexes(moduleId: ModuleId): void {
  if (_loadedModuleIds.has(moduleId)) return;
  const collection = SEED_COLLECTIONS.get(moduleId);
  if (!collection) return;
  const coreQuestions = collection.questions.map(seedToCore);

  // Push into ALL_QUESTIONS_CORE
  ALL_QUESTIONS_CORE.push(...coreQuestions);

  // Update all indexes for the new questions only
  for (const q of coreQuestions) {
    QUESTION_BY_ID.set(q.id, q);
    if (!QUESTIONS_BY_MODULE.has(q.module)) {
      QUESTIONS_BY_MODULE.set(q.module, []);
    }
    QUESTIONS_BY_MODULE.get(q.module)!.push(q);
    const mtKey = `${q.module}-${q.tier}`;
    if (!QUESTIONS_BY_MODULE_TIER.has(mtKey)) {
      QUESTIONS_BY_MODULE_TIER.set(mtKey, []);
    }
    QUESTIONS_BY_MODULE_TIER.get(mtKey)!.push(q);
    const msKey = `${q.module}-${q.subSkill}`;
    if (!QUESTIONS_BY_MODULE_SUBSKILL.has(msKey)) {
      QUESTIONS_BY_MODULE_SUBSKILL.set(msKey, []);
    }
    QUESTIONS_BY_MODULE_SUBSKILL.get(msKey)!.push(q);
    if (!SUBSKILLS_BY_MODULE.has(q.module)) {
      SUBSKILLS_BY_MODULE.set(q.module, new Set());
    }
    SUBSKILLS_BY_MODULE.get(q.module)!.add(q.subSkill);
    if (!QUESTIONS_BY_FAMILY.has(q.familyId)) {
      QUESTIONS_BY_FAMILY.set(q.familyId, []);
    }
    QUESTIONS_BY_FAMILY.get(q.familyId)!.push(q);
  }

  // Invalidate CITO cache so next access recomputes including new questions
  _citoEligibleCache = null;
  _loadedModuleIds.add(moduleId);
}

/**
 * Initialize the question loader: load seeds, build core array + all indexes.
 * Must be called once at app startup. After this resolves, all synchronous
 * exports (ALL_QUESTIONS_CORE, QUESTION_BY_ID, etc.) are populated.
 *
 * Bundle Optimization Phase 2 — mei 2026.
 */
export async function initializeQuestionLoader(): Promise<void> {
  if (_questionLoaderReady) return; // idempotent

  // Step 1: Ensure seeds are loaded
  if (!isSeedSystemReady()) {
    await initializeSeedCollections();
  }

  // Step 2: Add each module's questions to indexes (skips already-loaded ones)
  for (const [moduleId] of SEED_COLLECTIONS) {
    addModuleToIndexes(moduleId);
  }

  // Step 3: Data schema validation — DEV ONLY
  if (import.meta.env?.DEV) {
    let _dataSchemaWarnings = 0;
    for (const q of ALL_QUESTIONS_CORE) {
      if (q.data) {
        const result = validateQuestionData(q.questionType, q.data);
        if (!result.valid) {
          _dataSchemaWarnings++;
          debug(`[DataSchema] ${q.id}: ${result.errors.join('; ')}`);
        }
      }
    }
    if (_dataSchemaWarnings > 0) {
      debug(`[DataSchema] ${_dataSchemaWarnings} questions have data schema warnings`);
    }
  }
  _questionLoaderReady = true;

  // Step 5: Dev logging (after indexes built)
  if (import.meta.env?.DEV) {
    try {
      const allErrors = ALL_QUESTIONS_CORE.flatMap(validateQuestion);
      if (allErrors.length > 0) {
        group('❌ Question Taxonomy Validation Errors');
        allErrors.slice(0, 30).forEach(error => logError(error));
        if (allErrors.length > 30) {
          logError(`... and ${allErrors.length - 30} more errors`);
        }
        groupEnd();
      }
      const stats = getSeedStats();
      debug(`✅ Question Loader v4.2-lazy: ${stats.totalQuestions} questions across ${Object.keys(stats.byModule).length} modules ` + `(${getCitoEligibleQuestions().length} CITO-eligible)`);
    } catch (err) {
      logError('Question validation failed during init:', err);
    }
  }
}

// ============================================================
// CITO-eligible questions (lazy — computed on first access)
// ============================================================

let _citoEligibleCache: Question[] | null = null;

/** CITO-eligible questions subset (lazy-initialized) */
export function getCitoEligibleQuestions(): Question[] {
  if (!_citoEligibleCache) {
    _citoEligibleCache = ALL_QUESTIONS_CORE.filter(isCitoEligible);
  }
  return _citoEligibleCache;
}

/**
 * @deprecated Use getCitoEligibleQuestions() instead.
 * Lazy alias — no longer eagerly computed at module load.
 */
export const CITO_ELIGIBLE_QUESTIONS: Question[] = [];

// ============================================================
// Index Query Helpers
// ============================================================

/** Get questions for a module (indexed) */
export function getModuleQuestions(moduleId: ModuleId): Question[] {
  return QUESTIONS_BY_MODULE.get(moduleId) || [];
}

/** Get questions for a module+tier (indexed, authoritative) */
export function getModuleTierQuestions(moduleId: ModuleId, tier: number): Question[] {
  return QUESTIONS_BY_MODULE_TIER.get(`${moduleId}-${tier}`) || [];
}

/** Get questions for a module+subSkill (indexed) */
export function getModuleSubSkillQuestions(moduleId: ModuleId, subSkill: string): Question[] {
  return QUESTIONS_BY_MODULE_SUBSKILL.get(`${moduleId}-${subSkill}`) || [];
}

/** Get questions for a module+subSkill+tier (indexed, composed) */
export function getModuleSubSkillTierQuestions(moduleId: ModuleId, subSkill: string, tier: number): Question[] {
  const subSkillQuestions = QUESTIONS_BY_MODULE_SUBSKILL.get(`${moduleId}-${subSkill}`) || [];
  return subSkillQuestions.filter(q => q.tier === tier);
}

// ============================================================
// Question Retrieval
// ============================================================

/**
 * Get a single question by ID (includes feedback and visual aids).
 * Since v4.2, ALL_QUESTIONS_CORE already contains all feedback/aids data.
 */
export function getQuestionWithExplanation(questionId: string): Question | null {
  const q = QUESTION_BY_ID.get(questionId);
  if (!q) {
    logError(`QUESTION ERROR: Question not found: ${questionId}`);
    return null;
  }
  return q;
}

/**
 * Get all questions for a module.
 */
export function getQuestionsByModule(moduleId: ModuleId): Question[] {
  return QUESTIONS_BY_MODULE.get(moduleId) || [];
}

// ============================================================
// Full Question Retrieval
// ============================================================

/**
 * Get all questions.
 * Since v4.2, returns ALL_QUESTIONS_CORE directly (no copy overhead).
 */
export function getAllQuestions(): Question[] {
  return ALL_QUESTIONS_CORE;
}

// ============================================================
// Tier-Based Retrieval (Authoritative)
// ============================================================

/**
 * Get questions for a specific module and tier.
 */
export function getQuestionsForTier(moduleId: ModuleId, tier: number): Question[] {
  return QUESTIONS_BY_MODULE_TIER.get(`${moduleId}-${tier}`) || [];
}

/**
 * Get questions for a specific familyId.
 */
export function getQuestionsForFamily(familyId: string): Question[] {
  return QUESTIONS_BY_FAMILY.get(familyId) || [];
}

/**
 * Get questions for a familyId within a tier range.
 */
export function getQuestionsForFamilyInTierRange(familyId: string, minTier: number, maxTier: number): Question[] {
  return (QUESTIONS_BY_FAMILY.get(familyId) || []).filter(q => q.tier >= minTier && q.tier <= maxTier);
}

// ============================================================
// Seed Collection Stats
// ============================================================

/** Per-module stats shape */
interface ModuleStats {
  count: number;
  families: number;
  tierRange: [number, number];
  questionTypes: string[];
}

/**
 * Get statistics about the seed collections.
 */
export function getSeedStats(): {
  totalQuestions: number;
  byModule: Record<string, ModuleStats>;
} {
  const byModule: Record<string, ModuleStats> = {};
  for (const [moduleId, collection] of SEED_COLLECTIONS) {
    const families = new Set<string>();
    const tiers = new Set<number>();
    const types = new Set<string>();
    for (const q of collection.questions) {
      families.add(q.familyId);
      tiers.add(q.tier);
      types.add(q.questionType);
    }
    byModule[moduleId] = {
      count: collection.questions.length,
      families: families.size,
      tierRange: tiers.size > 0 ? [Math.min(...tiers), Math.max(...tiers)] : [0, 0],
      questionTypes: Array.from(types).sort()
    };
  }
  return {
    totalQuestions: ALL_QUESTIONS_CORE.length,
    byModule
  };
}

// ============================================================
// Exports
// ============================================================

export { MODULES };

// Development logging moved into initializeQuestionLoader() — mei 2026.